import React, { FC, FormEvent, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../redux/redux.hooks';
import { loginUser } from '../reducers/auth.reducer';
import client from '../clients/client';
import { useToast } from '../components/toast/ToastProvider';

type AuthProps = {};

type AuthOptions = {
  google: boolean;
  password: boolean;
};

// Set by the Google callback in api/views/google_auth_view.py
const googleErrors: Record<string, string> = {
  google: 'Google sign-in failed. Please try again.',
  domain: 'Sign in with your Easysize Google Workspace account.',
  disabled: 'This account has been disabled.',
};

const Auth: FC<AuthProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const authStatus = useAppSelector((state) => state.authReducer.status);
  const [options, setOptions] = useState<AuthOptions | null>(null);
  const [username, setUsername] = useState<string>('admin');
  const [password, setPassword] = useState<string>('');
  const toast = useToast();

  const [usernameHasError, setUsernameHasError] = useState<boolean>(false);
  const [passwordHasError, setPasswordHasError] = useState<boolean>(false);

  const usernameError = 'The username is required';
  const passwordError = 'Invalid password';

  const usernameBlur = () => {
    setUsernameHasError(username.length === 0);
  };

  const passwordBlur = () => {
    setPasswordHasError(password.length === 0);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await dispatch(loginUser({ username, password })).unwrap();
  };

  useEffect(() => {
    client
      .get<AuthOptions>('/auth/options')
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions({ google: true, password: false }));
  }, []);

  useEffect(() => {
    const googleError = googleErrors[searchParams.get('error') ?? ''];
    if (googleError) {
      toast.show(googleError, { isError: true });
    }
  }, [searchParams, toast]);

  useEffect(() => {
    if (authStatus === 'successful') {
      navigate('/');
    } else if (authStatus === 'error') {
      toast.show('Login failed', { isError: true });
    }
  }, [authStatus, navigate, toast]);

  return (
    <PageWrapper>
      <s-page heading='Authentication'>
        <CardContainer>
          {options?.google && (
            <s-section heading='Sign in'>
              <s-button variant='primary' href='/api/auth/google/login'>
                Sign in with Google
              </s-button>
            </s-section>
          )}
          {options?.password && (
            <s-section heading='Password login (local development only)'>
              <form onSubmit={onSubmit}>
                <s-stack gap='base'>
                  <s-text-field
                    id='username'
                    name='username'
                    label='username'
                    value={username}
                    onInput={(event) => setUsername(event.currentTarget.value)}
                    onBlur={usernameBlur}
                    error={usernameHasError ? usernameError : ''}
                    autocomplete='off'
                  />
                  <s-password-field
                    id='password'
                    name='password'
                    label='password'
                    value={password}
                    onInput={(event) => setPassword(event.currentTarget.value)}
                    onBlur={passwordBlur}
                    error={passwordHasError ? passwordError : ''}
                    autocomplete='off'
                  />
                  <s-button
                    type='submit'
                    disabled={username.length === 0 || password.length === 0}
                    loading={authStatus === 'loading'}
                  >
                    Submit
                  </s-button>
                </s-stack>
              </form>
            </s-section>
          )}
        </CardContainer>
      </s-page>
    </PageWrapper>
  );
};

export default Auth;

const PageWrapper = styled.div`
  height: 100vh;
  width: 100vw;
`;

const CardContainer = styled.div`
  width: 80%;
`;
